import {
  Colors,
  Dropdown,
  Icon,
  MediaQuery,
  TextSkeleton,
  skeletonFadeAnimation,
  FontFamily,
  FontWeight,
  BodyHeavy,
  Caption,
  CaptionHeavy,
  Button,
  CommentInput,
  Theme,
  CommentInputTextAreaContainer,
} from '@pelando/components';
import styled, { css } from 'styled-components';

export const defaultLateralSpacing = css`
  padding-right: 16px;
  padding-left: 16px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    padding-right: 32px;
    padding-left: 32px;
  }
`;

export const defaultCardSpacing = css`
  padding: 8px 16px 4px 16px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    padding-right: 32px;
    padding-left: 32px;
  }
`;

export const ActionsButton = styled.button`
  background: none;
  word-break: keep-all;
  display: inline-flex;
  align-items: center;
  ${Caption}
`;

export const CommentList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

export const CommentInputWrapper = styled.div`
  margin-top: 8px;
`;

export const CommentInputContent = styled.div`
  ${defaultLateralSpacing}
  margin-top: 16px;

  &[data-tabs='true'] {
    position: fixed;
    background-color: rgb(${Theme.colors.Juliet});
    z-index: 2;
    border-radius: 16px 16px 0px 0px;
    padding-top: 16px;
    padding-bottom: 12px;
    margin-top: unset;
    border: 1px solid rgb(${Theme.colors.Romeu});
    bottom: 0px;
    width: 100%;
  }
`;

export const CustomCommentInput = styled(CommentInput)`
  ${CommentInputTextAreaContainer} {
    background-color: rgb(${Theme.colors.Mike});
    border: 1px solid rgb(${Theme.colors.Golf});

    &:hover {
      background-color: rgb(${Theme.colors.Oscar});
      border: 1px solid rgb(${Theme.colors.November});
    }
  }
`;

export const FixedCommentsArea = styled.div`
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 0;

  @media ${MediaQuery.SCREEN_MD_UP} {
    border-bottom: 2px solid rgb(${Theme.colors.Hotel});
  }
`;

export const PrivateCommentsArea = styled.div`
  padding-bottom: 16px;
  &:empty {
    padding: unset;
  }
`;

export const AllCommentsArea = styled.div`
  padding-bottom: 16px;
`;

export const SectionTitle = styled.h3`
  ${BodyHeavy};
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  ${defaultLateralSpacing}
`;

const commentStyle = css`
  font-size: 18px;
  margin-left: 8px;
`;

export const TopCommentsIcon = styled(Icon)`
  ${commentStyle}
  color: rgb(${Theme.colors.Brand});
`;

export const AllCommentsIcon = styled(Icon)`
  ${commentStyle}
  color: rgb($${Theme.colors.FoxTrot});
`;

export const LockedBannerContainer = styled.div`
  height: 48px;
  width: 100%;
  display: flex;
  padding: 16px 22px;
  align-items: center;
  background-color: rgb(${Theme.colors.Lima});
  color: rgb(${Colors.Graphite});

  ${BodyHeavy}

  i {
    margin-right: 22px;
    color: rgb(${Theme.colors.Brand});
  }

  @media ${MediaQuery.SCREEN_MD_UP} {
    padding: 16px 32px;
  }
`;

export const Author = styled.span`
  ${CaptionHeavy};
  text-decoration: none;
  color: rgb(${Theme.colors.Alpha});
`;

export type CommentContainerSkeletonProps = {
  animate?: boolean;
  isFirstLoading?: boolean;
};

export const SkeletonDivider = styled.div`
  width: 100%;
  border-bottom: 0;

  @media ${MediaQuery.SCREEN_MD_UP} {
    border-bottom: 2px solid rgb(${Theme.colors.Hotel});
  }
`;

export const SkeletonInputWrapper = styled.div`
  ${defaultLateralSpacing}
  margin: 16px 0;
`;

export const SkeletonInputCommentSideWrapper = styled.div`
  padding: 0 16px 16px;
  overflow: hidden;

  @media ${MediaQuery.SCREEN_MD_UP} {
    padding: 0 24px 0 32px;
  }
`;

export const SkeletonCommentSideHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
`;

export const SkeletonTitleWrapper = styled.div`
  ${defaultLateralSpacing}
`;

export const CommentContainerSkeleton = styled.div<CommentContainerSkeletonProps>`
  ${({ animate }) =>
    skeletonFadeAnimation({ animate, disableBackground: true })}
`;

export const CommentSideContainerSkeleton = styled(CommentContainerSkeleton)`
  margin-left: 8px;
  display: none;
  width: 100%;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
  }
`;

export const SectionTitleSkeleton = styled(TextSkeleton)`
  margin-bottom: 16px;
  margin-top: 16px;
`;

export const DropdownItemShare = styled(Dropdown.Item)`
  @media ${MediaQuery.SCREEN_MD_UP} {
    display: none;
  }
`;

export const DropdownItemCopy = styled(Dropdown.Item)`
  display: none;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
  }
`;

export const CommentContainer = styled.div`
  background-color: rgb(${Theme.colors.Kilo});
  position: relative;
`;

export const CommentCount = styled.h2`
  ${BodyHeavy}
  font-family: ${FontFamily.DM_SANS};
  font-weight: ${FontWeight.BOLD};
`;

export const CommentInputHeader = styled.div`
  ${defaultLateralSpacing}
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${MediaQuery.SCREEN_MD_UP} {
    margin-bottom: 16px;
  }
`;

export const CommentInputHeaderLeftContent = styled.div`
  flex-direction: row;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  @media ${MediaQuery.SCREEN_MD_UP} {
    justify-content: unset;
    align-items: center;
    flex-direction: row;
    margin-bottom: 8px;
  }
`;

export const CommentInputHeaderRightContent = styled.div`
  display: none;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
  }
`;

export type FollowButtonProps = {
  isSaved?: boolean;
};

const notSavedStyle = css`
  color: rgb(${Theme.colors.Brand});

  i,
  &:hover {
    color: rgb(${Theme.colors.Brand}) !important;
  }
`;

export const FollowButton = styled(Button)<FollowButtonProps>`
  ${CaptionHeavy}
  border: none;
  padding: 0;
  min-height: 0;

  ${({ isSaved }) => !isSaved && notSavedStyle}
`;

export const VerticalDivider = styled.div`
  display: none;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
    height: 16px;
    width: 2px;
    background-color: rgb(${Theme.colors.November});
    margin: 0 12px;
  }
`;

export const CommentsArea = styled.div`
  max-height: 454px;
  height: 454px;
  overflow-y: auto;

  &[data-empty='true'] {
    border: 1px solid rgb(${Theme.colors.FoxTrot});
    border-radius: 16px;
    background: linear-gradient(
      270deg,
      rgb(${Theme.colors.Lima}),
      rgba(255, 255, 255, 1)
    );
    margin-left: 32px;
    margin-right: 32px;
  }

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    display: none;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(${Colors.Silver}, 1);
    border-radius: 5px;
  }

  ::-webkit-scrollbar-track {
    background-color: rgba(${Colors.Silver}, 1);
  }

  scrollbar-width: thin;
  scrollbar-color: rgba(${Colors.Silver}, 1) rgba(${Colors.White});

  -ms-overflow-style: -ms-autohiding-scrollbar;
`;

export const CommentInputArea = styled.div`
  border: 1px solid rgba(${Theme.colors.Golf}, 1);
  border-radius: 16px;
  padding: 16px;
  margin-left: 32px;
  margin-right: 32px;
  margin-top: 12px;
  background-color: rgba(${Theme.colors.Papa}, 1);
  position: relative;

  &[data-empty='true'] {
    border: 1px solid rgba(${Theme.colors.FoxTrot}, 1);
    margin-top: -30px;
    margin-left: 48px;
    margin-right: 48px;
  }
`;

export const SideCommentsContent = styled.div`
  max-width: 700px;
  @media (${MediaQuery.SCREEN_XL}) {
    min-width: 700px;
  }
`;

export const SideCommentsHeader = styled.div`
  ${defaultLateralSpacing}
  align-items: center;

  @media ${MediaQuery.SCREEN_MD_UP} {
    margin-bottom: 16px;
  }
`;

export const ContainerInput = styled.div`
  position: relative;
`;
